import { useTranslation } from "react-i18next";
import { matchPath, useLocation } from "react-router-dom";

import type { BreadcrumbItem } from "@/components/layout/ShellBar";
import { useProject } from "@/hooks/useApi";

const CONFIG_SERVER = "/projects/:projectId/agents/configuration-server";
const CLIENT_PDF = "/projects/:projectId/diagnostic/pdf";

// Etapas do Configuration Server: slug da rota → rótulo (chave i18n em inglês).
const CONFIG_SERVER_STAGES: Record<string, string> = {
  extraction: "Extraction",
  management: "Management",
  execution: "Execution",
};

/**
 * Breadcrumb do cabeçalho calculado pela rota, para as telas que moram dentro
 * do AppLayout. Hoje cobre o Configuration Server (central e as três etapas) e a
 * montagem do PDF do cliente (#1772); o resto devolve `undefined` e o cabeçalho
 * segue sem trilha.
 */
export function useShellBreadcrumbs(): BreadcrumbItem[] | undefined {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const central = matchPath({ path: CONFIG_SERVER, end: true }, pathname);
  const stage = matchPath({ path: `${CONFIG_SERVER}/:stage`, end: true }, pathname);
  const clientPdf = matchPath({ path: CLIENT_PDF, end: true }, pathname);
  const projectId =
    central?.params.projectId ?? stage?.params.projectId ?? clientPdf?.params.projectId;
  // Hooks sempre rodam; sem projeto o `enabled` da query a mantém parada.
  const { data: project } = useProject(projectId);

  if (!projectId) return undefined;
  const stageLabel = stage ? CONFIG_SERVER_STAGES[stage.params.stage ?? ""] : undefined;
  if (stage && !stageLabel) return undefined;

  const crumbs: BreadcrumbItem[] = [
    { label: t("Projects"), to: "/projects" },
    { label: project?.name ?? t("Project"), to: `/projects/${projectId}` },
  ];
  if (clientPdf) {
    // #1772: a montagem do PDF do cliente mora no Diagnóstico.
    crumbs.push({ label: t("Diagnostic"), to: `/projects/${projectId}/diagnostic` });
    crumbs.push({ label: t("PDF for the client") });
  } else if (stageLabel) {
    crumbs.push({ label: t("Configuration Server"), to: `/projects/${projectId}/agents/configuration-server` });
    crumbs.push({ label: t(stageLabel) });
  } else {
    crumbs.push({ label: t("Configuration Server") });
  }
  return crumbs;
}
